if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.quantityLimitReached = false;
      }

      connectedCallback() {
        this.syncQuantityLimitState();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        this.getValidatedCartQuantity(formData)
          .then((cartQuantity) => {
            if (cartQuantity === null) {
              this.error = true;
              return null;
            }

            this.syncQuantityLimitState(cartQuantity);
            return fetch(`${routes.cart_add_url}`, config);
          })
          .then((response) => {
            if (!response) return null;
            return response.json();
          })
          .then((response) => {
            if (!response) return;
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            this.quantityLimitReached = this.applyOptimisticQuantityLimit(formData);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            const cartUpdatePromise = this.error
              ? Promise.resolve()
              : publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                }).then(() => {
                  CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }

            return cartUpdatePromise;
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error && !this.quantityLimitReached) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      applyOptimisticQuantityLimit(formData) {
        const quantityInput = this.quantityInput;
        if (!quantityInput) return false;

        const addedQuantity = parseInt(formData.get('quantity') || quantityInput.value || '0', 10);
        if (!Number.isFinite(addedQuantity) || addedQuantity <= 0) return false;

        const cartQuantity = parseInt(quantityInput.dataset.cartQuantity || '0', 10) + addedQuantity;
        return this.syncQuantityLimitState(cartQuantity);
      }

      async getValidatedCartQuantity(formData) {
        const quantityInput = this.quantityInput;
        if (!quantityInput) return 0;

        const dataMax = quantityInput.dataset.max ? parseInt(quantityInput.dataset.max, 10) : null;
        const addedQuantity = parseInt(formData.get('quantity') || quantityInput.value || '0', 10);
        const variantId = parseInt(formData.get('id') || this.variantIdInput.value || '0', 10);

        if (!Number.isFinite(dataMax) || !Number.isFinite(addedQuantity) || !Number.isFinite(variantId)) {
          return parseInt(quantityInput.dataset.cartQuantity || '0', 10);
        }

        try {
          const response = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
          const cart = await response.json();
          const currentCartQuantity = Array.isArray(cart.items)
            ? cart.items.reduce((total, item) => total + (item.variant_id === variantId ? item.quantity : 0), 0)
            : 0;

          if (currentCartQuantity + addedQuantity > dataMax) {
            this.quantityLimitReached = this.syncQuantityLimitState(currentCartQuantity);
            this.handleErrorMessage(this.dataset.quantityLimitText || '個数制限');
            return null;
          }

          return currentCartQuantity;
        } catch (error) {
          console.error(error);
          return parseInt(quantityInput.dataset.cartQuantity || '0', 10);
        }
      }

      syncQuantityLimitState(forcedCartQuantity) {
        const quantityInput = this.quantityInput;
        if (!quantityInput) return false;

        const cartQuantity = Number.isFinite(forcedCartQuantity)
          ? forcedCartQuantity
          : parseInt(quantityInput.dataset.cartQuantity || '0', 10);
        const dataMin = parseInt(quantityInput.dataset.min || quantityInput.min || '1', 10);
        const dataMax = quantityInput.dataset.max ? parseInt(quantityInput.dataset.max, 10) : null;
        const step = parseInt(quantityInput.step || '1', 10);

        quantityInput.dataset.cartQuantity = String(cartQuantity);

        let nextValue = dataMin;
        const remainingMax = dataMax === null || Number.isNaN(dataMax) ? null : Math.max(dataMax - cartQuantity, 0);

        if (remainingMax !== null) nextValue = Math.min(nextValue, remainingMax);
        if (cartQuantity >= dataMin) nextValue = Math.min(nextValue, step);

        quantityInput.min = String(nextValue);
        if (remainingMax !== null) {
          quantityInput.max = String(remainingMax);
        } else {
          quantityInput.removeAttribute('max');
        }
        quantityInput.value = String(nextValue);

        const limitReached = remainingMax !== null && remainingMax <= 0;
        this.updateQuantityControlsState(quantityInput, limitReached);
        this.updateSubmitState(limitReached);
        quantityInput.dispatchEvent(new Event('input', { bubbles: true }));

        return limitReached;
      }

      updateQuantityControlsState(quantityInput, limitReached) {
        const quantityElement = quantityInput.closest('quantity-input');
        if (!quantityElement) return;

        const min = parseInt(quantityInput.min || '0', 10);
        const max = quantityInput.max ? parseInt(quantityInput.max, 10) : null;
        const value = parseInt(quantityInput.value || '0', 10);
        const minusButton = quantityElement.querySelector(".quantity__button[name='minus']");
        const plusButton = quantityElement.querySelector(".quantity__button[name='plus']");

        quantityInput.readOnly = limitReached;

        if (minusButton) {
          const disableMinus = limitReached || value <= min;
          minusButton.classList.toggle('disabled', disableMinus);
          minusButton.toggleAttribute('disabled', disableMinus);
        }

        if (plusButton) {
          const disablePlus = limitReached || (max !== null && value >= max);
          plusButton.classList.toggle('disabled', disablePlus);
          plusButton.toggleAttribute('disabled', disablePlus);
        }
      }

      updateSubmitState(limitReached) {
        const isSoldOut = this.dataset.isSoldOut === 'true';
        this.variantIdInput.disabled = limitReached || isSoldOut;
        const quantityLimitText = this.dataset.quantityLimitText || '個数制限';
        const soldOutText = this.dataset.soldOutText || window.variantStrings?.soldOut || '売り切れ';

        if (limitReached) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButtonText.textContent = quantityLimitText;
        } else if (isSoldOut) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButtonText.textContent = soldOutText;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }

        this.querySelectorAll('.shopify-payment-button__button, .shopify-payment-button [role="button"]').forEach((button) => {
          if (limitReached || isSoldOut) {
            button.setAttribute('disabled', 'disabled');
            button.setAttribute('aria-disabled', 'true');
            button.tabIndex = -1;
          } else {
            button.removeAttribute('disabled');
            button.removeAttribute('aria-disabled');
            button.removeAttribute('tabindex');
          }
        });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      get quantityInput() {
        return (
          this.querySelector(`.quantity__input[form="${this.form.id}"]`) ||
          this.closest('product-info')?.querySelector(`.quantity__input[form="${this.form.id}"]`) ||
          this.closest('product-info')?.querySelector('.product-form__quantity .quantity__input') ||
          this.form.querySelector('[name="quantity"]')
        );
      }
    }
  );
}
